/**
 * Super-Admin → Tenants
 *
 * The Discovery-owned tenant control plane (Phase A from
 * docs/architecture/13-CORE-DISCOVERY-INTEGRATION-MAP.md §5.1). This
 * page is the Path B self-provisioning surface: a platform admin types
 * a slug + name + industry hints + regulations and Discovery creates a
 * row in the local `tenants` table with origin='self_provisioned'.
 *
 * Path A (Core bootstrap) and Path C (Core attach) are NOT exposed here
 * - they ship in Phase B as separate endpoints. The rows they create
 * surface in this list with origin='core_bootstrap' or 'core_attached'
 * respectively, and are READ-ONLY here (rows can be viewed but the
 * Core-managed fields can't be edited from this UI).
 *
 * Platform-admin role required: platform_super_admin / platform_ops /
 * platform_support. The api-controller enforces this via
 * requirePlatformAdmin; the UI hides write actions when actorRole isn't
 * one of these.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Clock,
  Globe,
  Loader2,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Trash2,
  XCircle,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  createTenant,
  listTenants,
  ORIGIN_META,
  STATUS_META,
  terminateTenant,
  updateTenant,
  type Tenant,
  type TenantOrigin,
  type TenantStatus,
} from '@/lib/tenants-admin-api';

export function Tenants(): JSX.Element {
  const [rows, setRows] = useState<Tenant[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [originFilter, setOriginFilter] = useState<TenantOrigin | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<TenantStatus | 'all'>('all');
  const [showCreate, setShowCreate] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listTenants({
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(originFilter !== 'all' ? { origin: originFilter } : {}),
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      });
      setRows(r.tenants);
      setTotal(r.pagination.total);
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally {
      setLoading(false);
    }
  }, [search, originFilter, statusFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleStatusChange(t: Tenant, next: TenantStatus): Promise<void> {
    try {
      await updateTenant(t.id, { status: next });
      await refresh();
    } catch (err) {
      alert(`Status change failed: ${String((err as Error)?.message ?? err)}`);
    }
  }

  async function handleTerminate(t: Tenant): Promise<void> {
    if (!confirm(`Terminate tenant "${t.name}" (slug: ${t.slug})?\n\nStatus → terminating. Audit history preserved. Out of terminating is a TERMINAL state - no rollback.`)) return;
    try {
      await terminateTenant(t.id);
      await refresh();
    } catch (err) {
      alert(`Terminate failed: ${String((err as Error)?.message ?? err)}`);
    }
  }

  const activeCount = rows.filter((r) => r.status === 'active').length;
  const selfProvisionedCount = rows.filter((r) => r.origin === 'self_provisioned').length;
  const corePairedCount = rows.filter((r) => r.origin !== 'self_provisioned').length;

  return (
    <PageContainer>
      <PageHeader
        title="Tenants"
        subtitle="The Discovery-owned tenant control plane. Path B self-provisioning lives here; Path A (Core bootstrap) and Path C (Core attach) rows are read-only - managed via Phase B endpoints."
      />

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Kpi icon={<Building2 className="h-4 w-4" />} label="Total" value={String(total)} />
        <Kpi icon={<CheckCircle2 className="h-4 w-4 text-emerald-500" />} label="Active" value={String(activeCount)} />
        <Kpi
          icon={<Globe className="h-4 w-4 text-cyan-500" />}
          label={`Self-provisioned · Core-paired`}
          value={`${selfProvisionedCount} · ${corePairedCount}`}
        />
      </div>

      <GlassCard>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Tenant list</CardTitle>
              <CardDescription>{total} total · {rows.length} shown</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={refresh} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
              <Button size="sm" onClick={() => setShowCreate(true)}>
                <Plus className="-mt-0.5 mr-1 inline h-3 w-3" />
                New tenant
              </Button>
            </div>
          </div>
        </CardHeader>
        <div className="p-5 pt-0 space-y-3">
          {error && (
            <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-xs text-red-500">
              <AlertTriangle className="-mt-0.5 mr-1 inline h-3 w-3" />
              {error}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by slug or name…"
              className="flex-1 min-w-[200px] rounded-md border border-border bg-background/40 p-2 text-xs"
            />
            <select
              value={originFilter}
              onChange={(e) => setOriginFilter(e.target.value as TenantOrigin | 'all')}
              className="rounded-md border border-border bg-background/40 p-2 text-xs"
            >
              <option value="all">All origins</option>
              <option value="self_provisioned">Self-provisioned</option>
              <option value="core_bootstrap">Core bootstrap</option>
              <option value="core_attached">Core attached</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as TenantStatus | 'all')}
              className="rounded-md border border-border bg-background/40 p-2 text-xs"
            >
              <option value="all">All statuses</option>
              <option value="provisioning">Provisioning</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="terminating">Terminating</option>
            </select>
          </div>

          {/* Table */}
          {rows.length === 0 && !loading && (
            <div className="text-xs text-muted-foreground italic p-3">No tenants match your filters.</div>
          )}
          <div className="space-y-2">
            {rows.map((t) => (
              <TenantRow key={t.id} t={t} onStatus={handleStatusChange} onTerminate={handleTerminate} />
            ))}
          </div>
        </div>
      </GlassCard>

      {showCreate && (
        <CreateDialog onClose={() => setShowCreate(false)} onDone={refresh} />
      )}
    </PageContainer>
  );
}

function Kpi(props: { icon: React.ReactNode; label: string; value: string }): JSX.Element {
  return (
    <GlassCard>
      <div className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">{props.icon}<span>{props.label}</span></div>
        <div className="mt-2 text-2xl font-semibold">{props.value}</div>
      </div>
    </GlassCard>
  );
}

function TenantRow({
  t,
  onStatus,
  onTerminate,
}: {
  t: Tenant;
  onStatus: (t: Tenant, next: TenantStatus) => void;
  onTerminate: (t: Tenant) => void;
}): JSX.Element {
  const originMeta = ORIGIN_META[t.origin];
  const statusMeta = STATUS_META[t.status];
  const isTerminating = t.status === 'terminating';
  const isCorePaired = t.origin !== 'self_provisioned';

  return (
    <div className="rounded-lg border border-border bg-background/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold">{t.name}</span>
            <code className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-foreground/70">{t.slug}</code>
            <Badge variant="outline" className={`text-[10px] uppercase border-${originMeta.tone}-500/40 text-${originMeta.tone}-500`}>
              {originMeta.label}
            </Badge>
            <Badge variant={statusMeta.tone} className="text-[10px]">{statusMeta.label}</Badge>
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground font-mono">
            {t.id}
            {t.coreTenantId ? ` · Core: ${t.coreTenantId}` : ''}
          </p>
          {(t.industryHints.length > 0 || t.regulations.length > 0) && (
            <div className="mt-2 flex flex-wrap gap-1">
              {t.industryHints.map((h) => (
                <span key={h} className="text-[10px] px-1.5 py-0.5 rounded-md bg-cyan-500/10 text-cyan-500">{h}</span>
              ))}
              {t.regulations.map((r) => (
                <span key={r} className="text-[10px] px-1.5 py-0.5 rounded-md bg-violet-500/10 text-violet-500">{r}</span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!isTerminating && t.status === 'active' && (
            <Button size="sm" variant="ghost" title="Pause" onClick={() => onStatus(t, 'paused')}>
              <Pause className="h-3.5 w-3.5" />
            </Button>
          )}
          {!isTerminating && t.status === 'paused' && (
            <Button size="sm" variant="ghost" title="Resume" onClick={() => onStatus(t, 'active')}>
              <Play className="h-3.5 w-3.5" />
            </Button>
          )}
          {!isTerminating && t.status === 'provisioning' && (
            <Button size="sm" variant="ghost" title="Activate" onClick={() => onStatus(t, 'active')}>
              <CheckCircle2 className="h-3.5 w-3.5" />
            </Button>
          )}
          {!isTerminating && (
            <Button size="sm" variant="ghost" title="Terminate" onClick={() => onTerminate(t)}>
              <Trash2 className="h-3.5 w-3.5 text-red-500" />
            </Button>
          )}
          {isTerminating && <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-label="Terminal status" />}
        </div>
      </div>
      {isCorePaired && (
        <p className="mt-2 text-[10px] text-cyan-500/80 italic">
          Core-managed: status + Core fields are read-only here. Use Phase B endpoints / Core's wizard to mutate.
        </p>
      )}
    </div>
  );
}

/**
 * Bundle catalogue mirror for the wizard. Keeps the UI honest about
 * what modules each SKU entitles. The actual catalogue lives in
 * packages/industry-packs/src/bundles.ts (source of truth); this is
 * the customer-facing label set.
 */
const BUNDLE_CHOICES: Array<{
  sku: NonNullable<import('@/lib/tenants-admin-api').CreateTenantInput['sku']>;
  label: string;
  description: string;
  defaultModules: string[];
}> = [
  {
    sku: 'bfsi_enterprise',
    label: 'BFSI Enterprise',
    description: 'Full stack for banks, NBFCs, asset mgmt, insurance. Dedicated DB by default.',
    defaultModules: ['core', 'discovery', 'activity', 'ddr', 'compliance', 'foctta_platform'],
  },
  {
    sku: 'healthcare_enterprise',
    label: 'Healthcare Enterprise',
    description: 'Hospitals, pharma, clinical research. DDR opt-in.',
    defaultModules: ['core', 'discovery', 'activity', 'compliance', 'foctta_platform'],
  },
  {
    sku: 'enterprise',
    label: 'Enterprise (generic)',
    description: 'Full Discovery stack for non-BFSI verticals.',
    defaultModules: ['core', 'discovery', 'activity', 'ddr', 'compliance', 'foctta_platform'],
  },
  {
    sku: 'professional',
    label: 'Professional',
    description: 'Discovery + Compliance. No DAM / DDR.',
    defaultModules: ['core', 'discovery', 'compliance', 'foctta_platform'],
  },
  {
    sku: 'starter',
    label: 'Starter',
    description: 'Core privacy + platform only. No Discovery modules.',
    defaultModules: ['core', 'foctta_platform'],
  },
];

/** Module dependency rules - DDR cannot exist without DAM or DSPM. */
const MODULE_DEPS: Record<string, { requiresAnyOf: string[]; label: string }> = {
  ddr: {
    requiresAnyOf: ['discovery', 'activity'],
    label: 'DDR needs DAM (activity) or DSPM (discovery) to detect against.',
  },
};

function CreateDialog({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<void> }): JSX.Element {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [industryHints, setIndustryHints] = useState('');
  const [regulations, setRegulations] = useState('DPDPA');
  const [sku, setSku] = useState<NonNullable<import('@/lib/tenants-admin-api').CreateTenantInput['sku']>>('bfsi_enterprise');
  const [selectedModules, setSelectedModules] = useState<string[]>(BUNDLE_CHOICES[0]!.defaultModules);
  const [dataPlaneMode, setDataPlaneMode] = useState<'shared_rls' | 'dedicated_db_shared_cluster'>('shared_rls');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [warn, setWarn] = useState<string | null>(null);

  // When the SKU changes, snap selectedModules to the bundle's defaults.
  function pickSku(newSku: NonNullable<import('@/lib/tenants-admin-api').CreateTenantInput['sku']>): void {
    setSku(newSku);
    const bundle = BUNDLE_CHOICES.find((b) => b.sku === newSku);
    if (bundle) setSelectedModules([...bundle.defaultModules]);
    // BFSI / Healthcare default to dedicated DB.
    if (newSku === 'bfsi_enterprise' || newSku === 'healthcare_enterprise') {
      setDataPlaneMode('dedicated_db_shared_cluster');
    } else {
      setDataPlaneMode('shared_rls');
    }
  }

  function toggleModule(module: string): void {
    setWarn(null);
    setSelectedModules((cur) => {
      const has = cur.includes(module);
      if (has) {
        // Removing - check if anything depends on it
        const dependents = Object.entries(MODULE_DEPS)
          .filter(([dep, rule]) => cur.includes(dep) && rule.requiresAnyOf.includes(module))
          .filter(([dep, rule]) => {
            // Still satisfied if at least one OTHER required module remains
            const remaining = cur.filter((m) => m !== module);
            return !rule.requiresAnyOf.some((req) => remaining.includes(req));
          });
        if (dependents.length > 0) {
          setWarn(
            `Removing ${module} would break: ${dependents.map(([d]) => d).join(', ')}. Auto-removing dependents.`,
          );
          return cur.filter((m) => m !== module && !dependents.some(([d]) => d === m));
        }
        return cur.filter((m) => m !== module);
      } else {
        // Adding - auto-add required dependencies
        const rule = MODULE_DEPS[module];
        if (rule && !rule.requiresAnyOf.some((r) => cur.includes(r))) {
          // Auto-add the first available dependency (usually DSPM = discovery)
          const required = rule.requiresAnyOf[0]!;
          setWarn(`${rule.label} Auto-enabled ${required}.`);
          return [...cur, module, required].filter((v, i, a) => a.indexOf(v) === i);
        }
        return [...cur, module];
      }
    });
  }

  async function submit(): Promise<void> {
    setErr(null);
    setSubmitting(true);
    try {
      await createTenant({
        slug,
        name,
        industryHints: industryHints.split(',').map((s) => s.trim()).filter(Boolean),
        regulations: regulations.split(',').map((s) => s.trim()).filter(Boolean),
        modules: selectedModules,
        sku,
        dataPlaneMode,
      });
      await onDone();
      onClose();
    } catch (e) {
      setErr(String((e as Error)?.message ?? e));
    } finally {
      setSubmitting(false);
    }
  }

  const ALL_MODULES: Array<{ id: string; label: string; sub: string }> = [
    { id: 'discovery', label: 'DSPM - Discovery', sub: 'Scan + classify customer data' },
    { id: 'activity', label: 'DAM - Activity Monitoring', sub: 'Live query stream + UEBA baselines' },
    { id: 'ddr', label: 'DDR - Detection & Response', sub: 'Real-time threat detection (needs DAM or DSPM)' },
    { id: 'compliance', label: 'Compliance', sub: 'DSAR + breach + evidence rollups' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-xl border border-border bg-background p-6 shadow-2xl">
        <h3 className="mb-2 text-lg font-semibold">Provision new tenant (self-provisioned)</h3>
        <p className="mb-4 text-xs text-muted-foreground">
          Path B from <code>13-CORE-DISCOVERY-INTEGRATION-MAP.md</code>. Origin
          will be <code className="mx-1 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500">self_provisioned</code>.
          Templates auto-seed based on chosen bundle. CISO-ready: BFSI / Healthcare default to dedicated DB.
        </p>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {/*
              Phase 0 item 8 (2026-05-26) - replaced real customer name
              placeholders ("customer-oswal" / "the customer AMC") with
              synthetic placeholders. Customer names should never leak
              through super-admin UI even as placeholders - could be
              visible in screenshots, screen-shares, or partner demos.
            */}
            <Field label="Slug" value={slug} onChange={setSlug} placeholder="acme-bfsi" />
            <Field label="Display name" value={name} onChange={setName} placeholder="Acme BFSI Holdings" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Industry hints" value={industryHints} onChange={setIndustryHints} placeholder="bfsi, asset_management" />
            <Field label="Regulations" value={regulations} onChange={setRegulations} placeholder="DPDPA, SEBI_CSCRF" />
          </div>

          {/* Bundle picker */}
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Subscription bundle
            </label>
            <div className="grid grid-cols-1 gap-2">
              {BUNDLE_CHOICES.map((b) => (
                <button
                  key={b.sku}
                  type="button"
                  onClick={() => pickSku(b.sku)}
                  className={`text-left rounded-lg border p-3 transition-all ${
                    sku === b.sku
                      ? 'border-amber-500/50 bg-amber-500/5'
                      : 'border-border bg-background hover:border-muted-foreground/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">{b.label}</span>
                    {sku === b.sku && <CheckCircle2 className="h-4 w-4 text-amber-400" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{b.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Module checkboxes with dependency rules */}
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Modules
            </label>
            <div className="grid grid-cols-2 gap-2">
              {ALL_MODULES.map((m) => {
                const checked = selectedModules.includes(m.id);
                return (
                  <label
                    key={m.id}
                    className={`flex items-start gap-2 rounded-lg border p-2 cursor-pointer transition-all ${
                      checked
                        ? 'border-emerald-500/50 bg-emerald-500/5'
                        : 'border-border bg-background hover:border-muted-foreground/30'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleModule(m.id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{m.label}</div>
                      <div className="text-[10px] text-muted-foreground">{m.sub}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            {warn && (
              <p className="text-xs text-amber-400 flex items-start gap-1">
                <AlertTriangle className="-mt-0.5 inline h-3 w-3 flex-shrink-0" />
                <span>{warn}</span>
              </p>
            )}
          </div>

          {/* Data plane mode toggle */}
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Data-plane isolation
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setDataPlaneMode('shared_rls')}
                className={`rounded-lg border p-2 text-left transition-all ${
                  dataPlaneMode === 'shared_rls'
                    ? 'border-blue-500/50 bg-blue-500/5'
                    : 'border-border bg-background hover:border-muted-foreground/30'
                }`}
              >
                <div className="text-sm font-medium">T1 - Shared multi-tenant</div>
                <div className="text-[10px] text-muted-foreground">RLS-isolated. Cost-optimised.</div>
              </button>
              <button
                type="button"
                onClick={() => setDataPlaneMode('dedicated_db_shared_cluster')}
                className={`rounded-lg border p-2 text-left transition-all ${
                  dataPlaneMode === 'dedicated_db_shared_cluster'
                    ? 'border-purple-500/50 bg-purple-500/5'
                    : 'border-border bg-background hover:border-muted-foreground/30'
                }`}
              >
                <div className="text-sm font-medium">T2 - Dedicated DB</div>
                <div className="text-[10px] text-muted-foreground">CISO-grade. BFSI/Healthcare default.</div>
              </button>
            </div>
          </div>

          {err && (
            <p className="text-xs text-red-500">
              <XCircle className="-mt-0.5 mr-1 inline h-3 w-3" />
              {err}
            </p>
          )}
          <div className="mt-2 flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={submit} disabled={submitting || !slug || !name}>
              {submitting ? <Loader2 className="-mt-0.5 mr-1 inline h-3 w-3 animate-spin" /> : <Plus className="-mt-0.5 mr-1 inline h-3 w-3" />}
              Provision tenant
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): JSX.Element {
  return (
    <div>
      <label className="text-xs font-medium text-foreground/80">{props.label}</label>
      <input
        type="text"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        className="mt-1 w-full rounded-md border border-border bg-background/40 p-2 text-xs"
      />
    </div>
  );
}
